import { useRef } from "react"
import { useCopy } from "@/lib/contexts/appContext"
import Link from "@/components/common/menu/link"
import Menu from "@/components/common/menu/menu"
import { CaretRounded } from "@/components/common/icons/caret"
import classNames from "classnames/bind"
import styles from "./support.module.css"
import { submenu } from "./menu.module.css"

export default function Support({
  hasMounted,
  isMobile,
  isNarrow,
  setActive,
  isActive,
  inPrimary = false
}) {
  const copy = useCopy()
  const { support, localSupport } = copy?.header ?? {}

  const supportUsRef = useRef(null)
  const cx = classNames.bind(styles)

  if (!hasMounted) {
    return null
  }

  if (!inPrimary && (isMobile || isNarrow)) {
    return null
  }

  if (!support) {
    return null
  }

  let { url, target, label, items } = support

  if (!items) {
    return null
  }

  if (isMobile) {
    items = [{ url: url, target: target, label: label, items: [] }, ...items]
  }

  const handleClick = () => {
    if (!inPrimary) {
      return
    }

    setActive("supportUs", !isActive)
  }

  return (
    <div
      className={classNames(styles.support, { "in-primary": inPrimary })}
      aria-label={isActive ? "Stäng meny" : "Öppna meny"}
      aria-haspopup="true"
      aria-controls="support-menu"
      ref={supportUsRef}
      onMouseEnter={() =>
        !isMobile && !inPrimary && setActive("supportUs", true)
      }
      onMouseLeave={() =>
        !isMobile && !inPrimary && setActive("supportUs", false)
      }
      onClick={handleClick}>
      <MainItem
        inPrimary={inPrimary}
        isActive={isActive}
        label={label}
        url={url}
        items={items}
      />
      {!inPrimary && items.length > 0 && (
        <div
          className={cx([
            { submenu: true, on: isActive, "in-primary": inPrimary },
            submenu
          ])}>
          <nav id="support-menu">
            <SupportBox {...{ localSupport, inPrimary }} />
            <Menu
              menuClasses="primary"
              items={items}
              location="support"
              icon={true}
            />
          </nav>
        </div>
      )}
      {inPrimary && isActive && items.length > 0 && (
        <>
          <SupportBox {...{ localSupport, inPrimary }} />
          <Menu
            menuClasses="support"
            items={items}
            location="support"
            icon={true}
          />
        </>
      )}
    </div>
  )
}

const MainItem = ({ inPrimary, isActive, label, url, items }) => {
  const cx = classNames.bind(styles)

  if (!inPrimary) {
    return (
      <Link
        item={{ label: label, url: url }}
        className={cx({ "main-item": true })}
        legacyBehavior>
        {items.length > 0 && (
          <CaretRounded className={cx({ caret: true, rotate: isActive })} />
        )}
      </Link>
    )
  }

  return (
    <span className={cx({ "main-item": true })}>
      {label}
      {items.length > 0 && (
        <CaretRounded className={cx({ caret: true, rotate: isActive })} />
      )}
    </span>
  )
}

const SupportBox = ({ localSupport, inPrimary }) => {
  if (!localSupport || !localSupport?.showBox) {
    return null
  }

  const { heading, text } = localSupport
  const boxClasses = [
    {
      "mt-5 p-4 mb-2 rounded bg-green-50 text-black-100": !inPrimary,
      "p-4 mx-5 mb-2 rounded bg-green-50 text-black-100": inPrimary
    }
  ]

  return (
    <>
      <div className={classNames(boxClasses, "wysiwyg-content")}>
        <h3 className="mb-2 text-lg font-bold">{heading}</h3>
        <div dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </>
  )
}
